import React, { useState, useRef } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react-lite";
import { stores } from "../context";
import {
  UtensilsCrossed,
  Building2,
  Phone,
  BookOpen,
  Sparkles,
  ChevronDown,
  Wine,
  Utensils,
} from "lucide-react";

export const Menu: React.FC = observer(() => {
  const { product } = stores;
  const navigate = useNavigate();
  const { t } = useTranslation("common");

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleMouseEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsDropdownOpen(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setIsDropdownOpen(false);
    }, 200);
  };

  // EN: Switch active menu, reset selected category type, and close the dropdown popup
  const handleSelectMenu = (menuType: "FOOD" | "BAR") => {
    product.setActiveMenu(menuType);
    product.setSelectedType(null); // Reset specific category selection
    setIsDropdownOpen(false); // Close dropdown
    navigate("/menu"); // Navigate to the menu page
  };

  const getLinkClass = ({ isActive }: { isActive: boolean }) =>
    `inline-flex items-center gap-2 px-4 py-3 text-xs sm:text-sm font-serif font-medium tracking-wider uppercase transition-all duration-300 border-b-2 cursor-pointer ${
      isActive
        ? "border-amber-500 text-amber-400 font-bold bg-stone-900/40"
        : "border-transparent text-stone-300 hover:text-amber-300 hover:border-amber-500/40"
    }`;

  return (
    <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-50">
      <div className="flex items-center justify-center sm:justify-start space-x-1 sm:space-x-6 py-1">
        {/* DROPDOWN CONTAINER */}
        <div
          className="relative inline-block"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <NavLink
            to="/menu"
            className={getLinkClass}
            onClick={() => handleSelectMenu("FOOD")}
          >
            <UtensilsCrossed size={16} className="text-amber-500" />
            <span>{t("nav.menu", "Menu")}</span>
            <ChevronDown
              size={14}
              className={`transition-transform duration-200 ${
                isDropdownOpen ? "rotate-180 text-amber-400" : "text-stone-400"
              }`}
            />
          </NavLink>

          {/* EN: MENU TYPE SELECTION DROPDOWN */}
          {isDropdownOpen && (
            <div className="absolute top-full left-0 mt-1 w-60 z-50 transition-all duration-200">
              <div className="bg-stone-900/95 border border-amber-500/40 rounded-xl shadow-2xl py-2 backdrop-blur-xl max-h-[calc(100vh-120px)] overflow-y-auto no-scrollbar">
                <button
                  type="button"
                  onClick={() => handleSelectMenu("FOOD")}
                  className={`w-full flex items-center justify-between px-4 py-3 text-xs font-serif tracking-wider uppercase transition cursor-pointer hover:bg-amber-500/20 hover:text-amber-300 ${
                    product.activeMenu === "FOOD"
                      ? "text-amber-400 font-bold bg-amber-500/10"
                      : "text-stone-200"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <Utensils size={16} className="text-amber-500" />
                    <span>{t("nav.foodMenu", "Kuchnia")}</span>
                  </div>
                  {product.activeMenu === "FOOD" && (
                    <span className="w-2 h-2 rounded-full bg-amber-500 shadow-sm" />
                  )}
                </button>

                <div className="my-1 border-t border-stone-800/80" />

                <button
                  type="button"
                  onClick={() => handleSelectMenu("BAR")}
                  className={`w-full flex items-center justify-between px-4 py-3 text-xs font-serif tracking-wider uppercase transition cursor-pointer hover:bg-amber-500/20 hover:text-amber-300 ${
                    product.activeMenu === "BAR"
                      ? "text-amber-400 font-bold bg-amber-500/10"
                      : "text-stone-200"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <Wine size={16} className="text-amber-500" />
                    <span>{t("nav.bar", "Karta napojów")}</span>
                  </div>
                  {product.activeMenu === "BAR" && (
                    <span className="w-2 h-2 rounded-full bg-amber-500 shadow-sm" />
                  )}
                </button>
              </div>
            </div>
          )}
        </div>

        <NavLink to="/restaurant" className={getLinkClass}>
          <Building2 size={16} />
          <span>{t("nav.restaurant", "O nas")}</span>
        </NavLink>

        <NavLink to="/kontakt" className={getLinkClass}>
          <Phone size={16} />
          <span>{t("nav.contact", "Kontakt")}</span>
        </NavLink>

        <NavLink to="/recipes" className={getLinkClass}>
          <BookOpen size={16} />
          <span>{t("nav.recipes", "Przepisy")}</span>
        </NavLink>

        <NavLink to="/suggestions" className={getLinkClass}>
          <Sparkles size={16} />
          <span>{t("nav.suggestions", "Sugestie")}</span>
        </NavLink>
      </div>
    </nav>
  );
});

export default Menu;
